import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Body } from 'common/components/Typography/Body';
import {
    getStepperPrimaryColor,
    getStepperSecondaryColor,
    getStepperFontStyles,
} from 'common/entities/form/styleConfiguration/selectors';

import { FORM_SESSIONS_STEPS } from '../../constants/sessions';
import { getFormSessionStep } from '../../selectors';
import { withFormId } from '../FormIdContext';
import transformValueToPixels from '../../utils/transformValueToPixels';

const StepperContainer = styled.ol`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 1.5rem;
    padding: 0;
    list-style: none;
`;

const Step = styled.li`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 1.5rem;
    min-height: 1.5rem;
    width: ${({ fontSize }) => `calc(1.25 * ${transformValueToPixels(fontSize)})`};
    height: ${({ fontSize }) => `calc(1.25 * ${transformValueToPixels(fontSize)})`};
    border-radius: 50%;
    user-select: none;
    border: ${({ isActive, primaryColor }) => (isActive ? 'none' : `1px solid ${primaryColor}`)};
    color: ${({ isActive, primaryColor, secondaryColor }) => (isActive ? secondaryColor : primaryColor)};
    background-color: ${({ isActive, primaryColor, secondaryColor }) => (isActive ? primaryColor : secondaryColor)};
`;

const Divider = styled.div`
    width: 2rem;
    height: 1px;
    margin: 0 0.25rem;
    background-color: ${({ color }) => color};
`;

const StepText = styled(Body)`
    letter-spacing: 0;
    text-align: center;
    font-family: ${({ fontFamily }) => fontFamily};
    font-size: ${({ fontSize }) => transformValueToPixels(fontSize)};
`;

const FormStepper = ({ formId }) => {
    const currentStep = useSelector((state) => getFormSessionStep(state));
    const { font, fontSize } = useSelector((state) => getStepperFontStyles(state, formId));
    const primaryColor = useSelector((state) => getStepperPrimaryColor(state, formId));
    const secondaryColor = useSelector((state) => getStepperSecondaryColor(state, formId));

    const steps = useMemo(() => Object.values(FORM_SESSIONS_STEPS), []);

    return (
        <StepperContainer>
            {steps?.map((step, index) => (
                <React.Fragment key={step}>
                    <Step
                        isActive={currentStep === step}
                        primaryColor={primaryColor.value}
                        secondaryColor={secondaryColor.value}
                        fontSize={fontSize}
                        tabIndex={0}
                    >
                        {currentStep === step ? (
                            <VisuallyHidden.Root>Current step: {index + 1}</VisuallyHidden.Root>
                        ) : null}
                        <StepText tag={'span'} fontFamily={font.value} fontSize={fontSize} aria-hidden>
                            {index + 1}
                        </StepText>
                    </Step>
                    {index < steps.length - 1 ? <Divider color={primaryColor.value} /> : null}
                </React.Fragment>
            ))}
        </StepperContainer>
    );
};

FormStepper.propTypes = {
    formId: PropTypes.string.isRequired,
};

export default withFormId(FormStepper);
